import React, { CSSProperties, FC, useContext } from 'react'

import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'

import Input from '../../../../common/inputs/Input'
import MessageHelperComp from '../../../../../helpers/MessageHelper'
import { ScoreCardRow } from '../ScoreCardRow/ScoreCardRow'

import {
  IScoreCardInterface,
  IScoreCardSection,
} from '../../../../../models/score-cards/score-cards.interface'

import { MODAL_CONSTS } from '../../../../../const/modal-const'
import { HandleModalContext } from '../../../../../layout/mainlayout/MainLayout'
import { INITIAL_ROW } from '../../../../../const/score-card-const'

interface ScoreCardSectionProps {
  index: number
  section: IScoreCardSection
  register: UseFormRegister<IScoreCardInterface>
  watch: UseFormWatch<IScoreCardInterface>
  errors: FieldErrors<IScoreCardInterface>
  setValue: UseFormSetValue<IScoreCardInterface>
}

export const ScoreCardSection: FC<ScoreCardSectionProps> = ({
  index,
  section,
  register,
  watch,
  errors,
  setValue,
}) => {
  const handleModalContext = useContext(HandleModalContext)
  const handleModal = handleModalContext?.handleModal

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: section.id,
  })

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  }

  const onRemoveSection = () => {
    handleModal?.(true, MODAL_CONSTS.REMOVE_CONFIRMATION, {
      removeItem: () => {
        setValue(
          'sections',
          watch('sections')?.filter((field) => field.id !== section.id)
        )
      },
    })
  }

  const onAddRow = () => {
    setValue(`sections.${index}.rows`, [
      ...watch(`sections.${index}.rows`),
      {
        id: uuidv4(),
        ...INITIAL_ROW,
      },
    ])
  }

  return (
    <div className="pr-6 relative w-full" ref={setNodeRef} style={style}>
      <div className="mt-4 relative border border-SeabiscuitLightThemeColorD3 rounded-lg flex flex-wrap items-center justify-between p-3">
        <div
          {...attributes}
          {...listeners}
          className="hidden md:flex cursor-pointer opacity-50 hover:opacity-100 w-11 absolute z-10 left-0 p-3 top-1 transition-all"
        >
          <img src="/assets/cp_icons/Move.svg" alt="moveIcon" className="w-full" />
        </div>
        <div className="md:pl-8 relative w-full flex flex-col md:flex-row items-center justify-between gap-2">
          <Input
            disabled={watch(`sections.${index}.type`) === 'jump off'}
            className="p-0 border-none bg-transparent"
            register={register}
            placeholder="Round name..."
            name={`sections.${index}.name`}
            value={watch(`sections.${index}.name`)}
            valid={!!watch(`sections.${index}.name`)}
            validValueClassName="font-bold underline"
          />
          {!!errors?.sections?.[index]?.name?.message ? (
            <MessageHelperComp
              className="absolute z-0 bottom-1 left-0 translate-y-full"
              isError={true}
              message={errors?.sections?.[index]?.name?.message}
            />
          ) : null}
          <div className="ml-auto group whitespace-nowrap relative text-[14px] flex items-center text-SeabiscuitDark200ThemeColor">
            <span className="text-SeabiscuitDark200ThemeColor/50 mr-1">Round time limit</span>
            <label className="flex items-center cursor-pointer relative">
              <Input
                responsive
                defaultWidth="11px"
                type="number"
                containerClassName="!w-auto"
                className="p-0 border-none bg-transparent text-right"
                register={register}
                placeholder="0"
                name={`sections.${index}.maxTime`}
                value={watch(`sections.${index}.maxTime`)}
                valid={!!watch(`sections.${index}.maxTime`)}
                validValueClassName="text-SeabiscuitDark200ThemeColor"
              />
              <span className="pl-1">seconds</span>
              <div className="group-hover:bg-transparent absolute bottom-1 left-0 w-full h-[1px] bg-SeabiscuitDark200ThemeColor"></div>
            </label>
            {!!errors?.sections?.[index]?.maxTime?.message ? (
              <MessageHelperComp
                className="absolute z-0 bottom-1 right-0 translate-y-full"
                isError={true}
                message={errors?.sections?.[index]?.maxTime?.message}
              />
            ) : null}
          </div>
        </div>
        <SortableContext
          items={watch(`sections.${index}.rows`).map(({ id }) => id)}
          strategy={verticalListSortingStrategy}
        >
          {watch(`sections.${index}.rows`)?.map((row, rowIndex) => (
            <ScoreCardRow
              key={row.id}
              index={index}
              rowIndex={rowIndex}
              row={row}
              register={register}
              watch={watch}
              errors={errors}
              setValue={setValue}
            />
          ))}
        </SortableContext>
        <button
          className="cursor-pointer absolute top-3 flex items-center w-6 -right-8 hover:opacity-70 transition-all"
          onClick={onRemoveSection}
        >
          <img className="w-full" src="/assets/og_icons/Cancel.svg" alt="" />
        </button>
        <div className="mt-2 flex items-center justify-between gap-2 text-[14px] text-SeabiscuitDark200ThemeColor/50">
          <div className="flex items-center gap-2">
            <img src={`/assets/og_icons/Cancel.svg`} className="rotate-45 w-5" alt="plus icon" />
            <button type="button" className="underline hover:no-underline" onClick={onAddRow}>
              Add jump
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
